import type { ReactNode } from 'react'
import React from 'react'
import { Alert, HorizontalStack } from '@ztechapps/webui.util'
import { useFeatureFlag, useConfirmDialog } from '@ztechapps/common.hook'
import type { FeatureFlagKeyEnum } from '@ztechapps/common.contract'
import { BusyButton } from '@ztechapps/webui.button'
import { getWindow, humanize } from '@ztechapps/common.utils'

export type FeatureFlagAlertProps<T extends string = string> = {
  featureKey: FeatureFlagKeyEnum
  title?: string
  description: ReactNode
  buttonText?: string
  targetChoice: T
  currentChoice?: T
}

export const FeatureFlagAlert = <T extends string = string>({
  featureKey,
  title,
  description,
  buttonText = 'Switch',
  targetChoice,
  currentChoice,
}: FeatureFlagAlertProps<T>) => {
  const { selectedChoice, setChoice, loading } = useFeatureFlag<T>(featureKey)

  const { onConfirm } = useConfirmDialog({
    title: 'Switch Feature Flag',
    text: `Are you sure you want to switch to "${humanize(targetChoice)}"? The page will be refreshed.`,
    buttons: {
      confirm: {
        text: 'Yes, switch it!',
      },
    },
    confirmed: async () => {
      await setChoice(targetChoice)
      getWindow().location.reload()
    },
  })

  // Use provided currentChoice or fall back to hook's selectedChoice
  const effectiveCurrentChoice = currentChoice ?? selectedChoice

  // Only show alert if current choice is different from target choice
  if (effectiveCurrentChoice === targetChoice) {
    return null
  }

  title = title ?? humanize(featureKey)

  return (
    <Alert type="warning" className="mb-3" dismissible>
      <HorizontalStack gap={4} className=" align-items-center">
        <div>
          <strong>{title}</strong> - {description}
        </div>
        <BusyButton
          bsSize="sm"
          onClick={onConfirm}
          isBusy={loading}
          busyText="Switching..."
          text={buttonText}
          variant="primary"
        />
      </HorizontalStack>
    </Alert>
  )
}
